import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MessageCircle, Download, X } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import HomePage from "./pages/HomePage";
import AuthPage from "./pages/AuthPage";
import CatalogPage from "./pages/CatalogPage";
import ProductPage from "./pages/ProductPage";
import VendorShopPage from "./pages/VendorShopPage";
import CartPage from "./pages/CartPage";
import OrdersPage from "./pages/OrdersPage";
import VendorDashboard from "./pages/VendorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ProfilePage from "./pages/ProfilePage";
import MessagesPage from "./pages/MessagesPage";
import FavoritesPage from "./pages/FavoritesPage";

const ProtectedRoute = ({ children, role }) => {
  const { user } = useAuth();
  if (user === undefined) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#FF9900] border-t-transparent" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

// ── Bannière d'installation PWA ──────────────────────────────────────────────
function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const dismissed = useRef(false);

  useEffect(() => {
    // Déjà installé ou déjà refusé
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (sessionStorage.getItem("pwa_banner_dismissed")) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setShowBanner(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    dismissed.current = true;
    setShowBanner(false);
    sessionStorage.setItem("pwa_banner_dismissed", "1");
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed bottom-[80px] left-3 right-3 z-50 bg-[#1B5E20] text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 animate-[slideUp_0.3s_ease-out]"
      data-testid="pwa-install-banner"
      style={{ animation: "slideUp 0.3s ease-out" }}
    >
      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shrink-0">
        <img src="/logo192.png" alt="Jokkoleen" className="w-8 h-8 rounded-lg" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm leading-tight">Installer Jokkoleen</p>
        <p className="text-green-200 text-xs mt-0.5 leading-snug">Accès rapide depuis votre écran d'accueil</p>
      </div>
      <button
        onClick={handleInstall}
        className="bg-white text-[#1B5E20] font-bold text-xs px-3 py-2 rounded-xl hover:bg-green-50 transition-colors flex items-center gap-1.5 shrink-0"
        data-testid="pwa-install-btn"
      >
        <Download size={13} /> Installer
      </button>
      <button
        onClick={handleDismiss}
        className="text-green-200 hover:text-white transition-colors shrink-0 ml-1"
        data-testid="pwa-dismiss-btn"
        aria-label="Fermer"
      >
        <X size={18} />
      </button>
    </div>
  );
}

const AppLayout = ({ children }) => (
  <>
    <Header />
    <main className="min-h-screen">{children}</main>
    <BottomNav />
    <a
      href="https://wa.me/33777369462?text=Bonjour%2C%20int%C3%A9ress%C3%A9%20par%20votre%20offre%20sur%20Jokkoleen"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-[74px] right-4 md:hidden z-50 bg-[#25D366] text-white rounded-full p-3.5 shadow-xl hover:bg-[#20BD5A] active:scale-95 transition-all"
      data-testid="whatsapp-fab"
      aria-label="Contacter sur WhatsApp"
    >
      <MessageCircle size={22} />
    </a>
    <PWAInstallBanner />
  </>
);

function AppRoutes() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/catalogue" element={<CatalogPage />} />
        <Route path="/produit/:id" element={<ProductPage />} />
        <Route path="/boutique/:id" element={<VendorShopPage />} />
        <Route path="/panier" element={<CartPage />} />
        <Route path="/favoris" element={<FavoritesPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/commandes" element={
          <ProtectedRoute><OrdersPage /></ProtectedRoute>
        } />
        <Route path="/profil" element={
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        } />
        <Route path="/dashboard/vendeur" element={
          <ProtectedRoute><VendorDashboard /></ProtectedRoute>
        } />
        <Route path="/dashboard/admin" element={
          <ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRoutes />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
